/**
 * API Client Configuration
 *
 * This file configures Axios for making HTTP requests to the backend API.
 * It sets up interceptors for authentication, error handling, and request/response formatting.
 */
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

// Add Vite environment type definitions

// Define error response data structure
interface ApiErrorResponse {
  error?: string;
  message?: string;
  [key: string]: any;
}

/**
 * Resolve base URL for regular API endpoints
 * Priority:
 * 1. `VITE_API_URL` env var (must start with http/https)
 * 2. Fallback to `http://localhost:4001`
 */
function resolveApiBaseUrl(): string {
  // Development → backend runs on 4001
  if (import.meta.env.DEV) {
    return 'http://localhost:4001';
  }

  // In production, prefer explicit env var
  const envUrl = import.meta.env.VITE_API_URL;
  if (
    envUrl &&
    /^https?:\/\//i.test(envUrl) &&
    !/\/\/(?:localhost|127(?:\.\d{1,3}){3})/i.test(envUrl)
  ) {
    return envUrl.replace(/\/+$/, '');
  }

  // Fallback: same-origin (front & back behind NGINX)
  return window.location.origin.replace(/\/+$/, '');
}

// ---------------------------------------------------------------------------
// Resolve the base URL once and, in development, log it for quick diagnosis
// ---------------------------------------------------------------------------
const API_PREFIX = import.meta.env.PROD ? '/api' : '';
const BASE_URL = `${resolveApiBaseUrl()}${API_PREFIX}`;
if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.log('[API] baseURL =', BASE_URL);
}

/**
 * Resolve base URL for auth endpoints
 * Priority:
 * 1. `VITE_AUTH_API_URL` env var (must start with http/https)
 * 2. Fallback to the same base as the regular API (port 4001)
 */
// NOTE:
// We no longer use a separate auth server.  Auth endpoints live on the same
// backend (port 4001).  Keeping this helper for backward-compatibility in case
// someone still defines VITE_AUTH_API_URL, otherwise it falls back to the main
// API base URL.
// Create a base axios instance for regular API calls
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000, // 15 seconds timeout
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/* --------------------------------------------------------------------------
 * From now on we use a single Axios instance (`apiClient`) for both regular
 * and authentication endpoints.  Keeping `authClient` variable name for
 * minimal refactor effort but pointing it to the same instance so downstream
 * code keeps working.
 * ------------------------------------------------------------------------ */
// eslint-disable-next-line @typescript-eslint/naming-convention
const authClient: AxiosInstance = apiClient;

// Configure request interceptor for both clients
const configureRequestInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Get token from localStorage
      const token = localStorage.getItem('snippix_access_token');

      // If token exists, add it to the headers
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: AxiosError) => {
      // Handle request errors
      console.error('API Request Error:', error);
      return Promise.reject(error);
    }
  );
};

// Configure development-time logging for both clients
const configureDevLogging = (client: AxiosInstance, prefix: string) => {
  if (import.meta.env.DEV) {
    client.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
      // eslint-disable-next-line no-console
      console.log(`[${prefix}] → ${cfg.method?.toUpperCase()} ${cfg.baseURL}${cfg.url}`, cfg);
      return cfg;
    });

    client.interceptors.response.use(
      res => {
        // eslint-disable-next-line no-console
        console.log(`[${prefix}] ← ${res.status} ${res.config.url}`, res.data);
        return res;
      },
      err => {
        // eslint-disable-next-line no-console
        console.error(
          `[${prefix}] ← ERROR ${err.response?.status ?? ''} ${err.config?.url}`,
          err.response?.data ?? err.message
        );
        return Promise.reject(err);
      }
    );
  }
};

// Configure response interceptor with token refresh for both clients
const configureResponseInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Process successful responses
      return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      // Get the original request
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // ------------------------------------------------------------------
      // Automatic token refresh
      // ------------------------------------------------------------------
      // We refresh the token on *any* 401 response as long as:
      //   • the request hasn't already been retried ( _retry flag )
      //   • the failing request is NOT itself an auth endpoint
      //     ( /auth/login or /auth/refresh ) to avoid infinite loops
      // ------------------------------------------------------------------
      const reqUrl = (originalRequest.url || '').toString();
      const isAuthEndpoint = /\/auth\/(login|refresh)/.test(reqUrl);

      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;

        try {
          // Try to refresh the token - always use authClient for this
          const response = await authClient.post('/auth/refresh');
          const { accessToken } = response.data;

          // Update token in localStorage
          localStorage.setItem('snippix_access_token', accessToken);

          // Update the Authorization header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          // Retry the original request with the client that made it
          return client(originalRequest);
        } catch (refreshError) {
          // If refresh fails, clear auth data and reject
          localStorage.removeItem('snippix_access_token');
          localStorage.removeItem('snippix_user');

          // Redirect to login page
          window.location.href = '/login';

          return Promise.reject(refreshError);
        }
      }

      /* ------------------------------------------------------------------
       * Inactive account handling
       * ------------------------------------------------------------------
       * If the backend explicitly reports the account as inactive we force
       * a full logout and redirect to the login page so the UI state stays
       * consistent across tabs.
       * ------------------------------------------------------------------ */
      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        (error.response?.data?.error === 'Account inactive' ||
          error.response?.data?.message === 'Account inactive')
      ) {
        // Clear any stored auth credentials
        localStorage.removeItem('snippix_access_token');
        localStorage.removeItem('snippix_user');

        // Redirect to login (will also clear auth context in other tabs via storage event)
        window.location.href = '/login';

        return Promise.reject(error);
      }

      // Format error message for UI display
      let errorMessage = 'An unexpected error occurred';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Attach formatted error message to the error object
      const enhancedError = error as AxiosError & { displayMessage?: string };
      enhancedError.displayMessage = errorMessage;

      // Handle specific HTTP status codes
      switch (error.response?.status) {
        case 400:
          console.error('Bad Request:', errorMessage);
          break;
        case 401:
          console.error('Unauthorized:', errorMessage);
          break;
        case 403:
          console.error('Forbidden:', errorMessage);
          break;
        case 404:
          console.error('Not Found:', errorMessage);
          break;
        case 500:
          console.error('Server Error:', errorMessage);
          break;
        default:
          console.error('API Error:', errorMessage);
      }

      return Promise.reject(enhancedError);
    }
  );
};

// Apply configurations to both clients
configureRequestInterceptor(apiClient);
configureDevLogging(apiClient, 'API');
configureResponseInterceptor(apiClient);
// authClient is apiClient, above configs already applied

// API helper functions with explicit route handling
export const api = {
  // Auth-specific endpoints with explicit /auth prefix
  // These now go through the main backend on port 4001
  auth: {
    login: <T>(credentials: any, config?: AxiosRequestConfig) =>
      authClient.post<T>('/auth/login', credentials, config).then(res => res.data),

    refresh: <T>(refreshToken?: string, config?: AxiosRequestConfig) =>
      authClient.post<T>('/auth/refresh', { refreshToken }, config).then(res => res.data),

    logout: <T>(config?: AxiosRequestConfig) =>
      authClient.post<T>('/auth/logout', {}, config).then(res => res.data),

    me: <T>(config?: AxiosRequestConfig) =>
      authClient.get<T>('/auth/me', config).then(res => res.data),

    // Register new user (domain-restricted server-side)
    register: <T>(payload: any, config?: AxiosRequestConfig) =>
      authClient.post<T>('/auth/register', payload, config).then(res => res.data),
  },

  // General API endpoints - use the regular API server on port 4001
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then(res => res.data),

  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then(res => res.data),

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then(res => res.data),

  // Fetch binary data (e.g., ZIP) as Blob
  getBlob: (url: string, config?: AxiosRequestConfig): Promise<Blob> =>
    apiClient.get(url, { ...(config || {}), responseType: 'blob' }).then(res => res.data as Blob),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then(res => res.data),

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then(res => res.data),

  // ------------------------------
  // Admin settings - Allowed domains
  // ------------------------------
  settings: {
    allowedDomains: {
      // Fetch list of allowed domains
      list: <T>(config?: AxiosRequestConfig) =>
        apiClient.get<T>('/settings/allowed-domains', config).then(res => res.data),

      // Add one or multiple domains
      add: <T>(domains: string[] | string, config?: AxiosRequestConfig) => {
        const body = Array.isArray(domains) ? { domains } : { domain: domains };
        return apiClient.post<T>('/settings/allowed-domains', body, config).then(res => res.data);
      },

      // Remove by database ID
      removeById: <T>(id: string, config?: AxiosRequestConfig) =>
        apiClient.delete<T>(`/settings/allowed-domains/${id}`, config).then(res => res.data),

      // Remove by domain string
      removeByDomain: <T>(domain: string, config?: AxiosRequestConfig) =>
        apiClient
          .delete<T>('/settings/allowed-domains', { ...(config || {}), data: { domain } })
          .then(res => res.data),
    },
  },
};

// Export both clients for advanced use cases
export { apiClient, authClient };

// Default export is the main API client
export default apiClient;
